import * as Sentry from '@sentry/browser'
import { ApiResponse } from 'apisauce'
import { call, put } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import i18n from '../config/i18n'
import { setLoading, setResponse, setSuccess, setPagination, StateKey, PAGINATION_STATE_KEYS } from '../state/requests'
import { PartipostApiResponse } from '../services/api'
import { clearToken } from '../services/auth'
import { logout } from '../services/api/profile'
import { isUnknownError, isPermissionDeniedError, isUserNotInTeamError } from '../services/api/errors'
import { showSnackBar } from '../services/toast'
import { STATE_KEYS } from '../state/requests'
import { checkForError } from '../services/api/errors'
import { getProfileDetail } from 'state/profile'
import { setTeamFilter } from 'state/filter'

interface HandleRequestPayload<T1, T2> {
    stateKey: StateKey
    payload: T1
    ApiHandler: (params: T1) => Promise<ApiResponse<PartipostApiResponse<T2>>>
    onSuccess?: (response: ApiResponse<PartipostApiResponse<T2>>) => void
    onError?: (e: any) => void
    onRequest?: () => void
    onSettled?: () => void
}

const HANDLED_STATE_EXCEPTIONS = [
    STATE_KEYS.ResetPassword,
    STATE_KEYS.UpdatePassword,
    STATE_KEYS.Login,
    STATE_KEYS.CreateTeam,
    STATE_KEYS.UpdateTeam,
    STATE_KEYS.UpdateTeamType,
    STATE_KEYS.DeleteTeam,
    STATE_KEYS.CreateMember,
    STATE_KEYS.UpdateMember,
    STATE_KEYS.DeleteMember,
    STATE_KEYS.ResendMemberInvite,
    STATE_KEYS.CreateBrand,
    STATE_KEYS.DeleteBrand,
    STATE_KEYS.SavePartiposterTag,
    STATE_KEYS.SubmitCampaign,
    STATE_KEYS.UpdateCampaign,
    STATE_KEYS.PublishCampaign,
    STATE_KEYS.PaySubmission,
    STATE_KEYS.RejectSubmissionPayout,
    STATE_KEYS.BlockedCampaignCheck,
    STATE_KEYS.ExportProgress,
    STATE_KEYS.ExportPerformance,
    STATE_KEYS.ApproveSubmission,
    STATE_KEYS.RequestEditsForSubmission,
    STATE_KEYS.UpdateRegistrationForm,
    STATE_KEYS.MassRegistration,
    STATE_KEYS.VerifyToken,
    STATE_KEYS.DeleteCampaign,
    STATE_KEYS.VerifyEmail,
    STATE_KEYS.SignUp,
    STATE_KEYS.SignUpDetails,
    STATE_KEYS.InviteTeamMember,
    STATE_KEYS.SubmitCampaignTemplate,
    STATE_KEYS.TemplateSendToEdit,
    STATE_KEYS.TemplatePublish,
    STATE_KEYS.UnpublishGuidedTemplate,
    STATE_KEYS.UseGuidedTemplate,
    STATE_KEYS.DeleteCampaignTemplateData,
    STATE_KEYS.ExportTemplate,
    STATE_KEYS.UpdateCampaignSchedule,
    STATE_KEYS.SkipLoading,
    STATE_KEYS.UpdateBusinessDetails,
    STATE_KEYS.GetBusinessDetails,
]

const HANDLED_404_EXCEPTION = [
    STATE_KEYS.GetBusinessDetails,
    STATE_KEYS.CampaignSubmissionsSummary,
    STATE_KEYS.EndCampaign,
]

const AUTO_SAVED_EXCEPTIONS = [STATE_KEYS.UpdateRegistrationMarkAsViewed, STATE_KEYS.HighlightRegistration]

export function* handleRequest<T1, T2>({
    stateKey,
    payload,
    ApiHandler,
    onSuccess,
    onRequest,
    onError,
    onSettled,
}: HandleRequestPayload<T1, T2>): any {
    try {
        yield put(setLoading({ stateKey, isLoading: true }))
        yield put(setResponse({ stateKey, response: '' }))
        yield put(setSuccess({ stateKey, isSuccess: false }))
        const response = yield call(ApiHandler, payload)
        const responseIsReturned = response.data && response.data.success
        const deleteIsSuccess = response.status === 204
        if (HANDLED_404_EXCEPTION.indexOf(stateKey) < 0 && response.status === 404) {
            return yield put(push('/404'))
        }
        // for case 500 (html response) (not from backend api response), just throw undefined to display general error toast
        // this is to prevent error when treating response.data as object
        if (typeof response.data !== 'object') {
            throw undefined
        }

        if (responseIsReturned || deleteIsSuccess) {
            if (stateKey in PAGINATION_STATE_KEYS) {
                yield put(
                    setPagination({
                        stateKey,
                        count: response.data !== undefined ? response.data.data.count : null,
                        next: response.data !== undefined ? response.data.data.next : '',
                        previous: response.data !== undefined ? response.data.data.previous : '',
                    }),
                )
            }
            if (onRequest !== undefined) yield onRequest()
            if (onSuccess !== undefined) yield onSuccess(response)
            yield put(setSuccess({ stateKey, isSuccess: true }))
        } else if (response.data && !response.data.success) {
            //Auto save not show error toast
            if (AUTO_SAVED_EXCEPTIONS.indexOf(stateKey) > 0) {
                throw undefined
            }
            if (stateKey === STATE_KEYS.ApproveSubmission || stateKey === STATE_KEYS.RequestEditsForSubmission) {
                const isNotInBatchError = checkForError(
                    response.data ? response.data.errors : undefined,
                    'SUBMISSION_PROGRESS_INVALID',
                    'SUBMISSION_NOT_IN_BATCH',
                )
                if (isNotInBatchError) {
                    throw response.data ? response.data.errors : undefined
                }
            }
            if (isUserNotInTeamError(response.data.errors)) {
                yield put(setTeamFilter(''))
                window.location.reload()
            }
            if (isPermissionDeniedError(response.data.errors)) {
                showSnackBar({
                    title: i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle'),
                    type: 'error',
                })
                yield put(getProfileDetail({ stateKey: STATE_KEYS.SkipLoading }))
            } else if (HANDLED_STATE_EXCEPTIONS.indexOf(stateKey) < 0) {
                if (isUnknownError(response.data.errors)) {
                    showSnackBar({
                        title: i18n.t('errorMessage.general.unknownError'),
                        type: 'error',
                    })
                }
            }
            throw response.data ? response.data.errors : undefined
        } else {
            throw undefined
        }
    } catch (e) {
        if (((e as any)?.general_errors?.reasons?.indexOf('AUTHENTICATION_FAILED') ?? -1) >= 0) {
            yield call(clearToken)
            yield call(logout)
        }
        Sentry.withScope((scope: any) => {
            scope.setTag('api', stateKey)
            scope.setExtra('payload', payload)
            Sentry.captureException(e)
        })
        yield put(setResponse({ stateKey, response: e || 'Network error' }))
        if (onError !== undefined) yield onError(e)
    } finally {
        yield put(setLoading({ stateKey, isLoading: false }))
        if (onSettled !== undefined) yield onSettled()
    }
}
